import React, { Component } from "react";
import { Container, Grid, Header } from "semantic-ui-react";
import { specialCollectionsApi } from "@api/specialCollections/specialCollections";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { QueryBuildHelper } from "@components/SearchBar/QueryBuildHelper";
import {
  invenioConfig,
  setReactSearchKitInitialQueryState,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitUrlHandler,
} from "@config";
import history from "@history";
import SearchAggregationsCards from "@modules/SearchControls/SearchAggregationsCards";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import { BackOfficeRoutes } from "@routes/urls";
import { OverridableContext } from "react-overridable";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsList,
  ResultsLoader,
  SearchBar,
} from "react-searchkit";
import ClearFiltersButton from "@modules/SearchControls/ClearFiltersButton";
import SessionFiltersPersistence from "@modules/SearchControls/SessionFiltersPersistence";
import { SpecialCollectionListEntry } from "@pages/backoffice/SpecialCollections/SpecialCollectionListEntry";
import CascadingDropdownFilter from "@modules/SearchControls/overridden/CascadingDropdownElement";
import Qs from "qs";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

export class SpecialCollectionsSearch extends Component {
  modelName = "SPECIAL_COLLECTIONS";

  searchApi = new InvenioSearchApi({
    axios: {
      url: specialCollectionsApi.searchBaseURL,
      withCredentials: true,
    },
  });

  getRequestSerializer = () => {
    const requestSerializer = this.searchApi.requestSerializer;
    if (
      !requestSerializer ||
      typeof requestSerializer.serialize !== "function"
    ) {
      throw new Error(
        "requestSerializer is not configured correctly in SpecialCollectionsSearch."
      );
    }
    return requestSerializer;
  };

  handleExportClick = async (format, size, currentQueryState) => {
    if (format === "pdf") {
      return this.customExportPDF(currentQueryState, size);
    }

    // Handle CSV export with custom headers
    if (format === "csv") {
      return this.customExportCSV(currentQueryState, size);
    }

    try {
      const requestSerializer = this.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params["page"] = 1;
      params["format"] = format;
      const exportUrl = `${specialCollectionsApi.searchBaseURL}?${Qs.stringify(
        params,
        {
          arrayFormat: "repeat",
          encode: false,
        }
      )}`;
      window.open(exportUrl, "_blank");
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  customExportCSV = async (currentQueryState, size = 1000) => {
    try {
      const requestSerializer = this.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params["page"] = 1;
      params["size"] = size;
      params["sort"] = "_created";

      // Fetch JSON data instead of CSV
      const exportUrl = `${specialCollectionsApi.searchBaseURL}?${Qs.stringify(
        params,
        {
          arrayFormat: "repeat",
          encode: false,
        }
      )}`;

      const response = await fetch(exportUrl, { credentials: "include" });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const hits = data.hits?.hits || [];

      // Define CSV headers
      const headers = [
        "Title",
        "Author(s)",
        "Publication Year",
        "Collection Type",
        "Department",
        "Program",
      ];

      // Build CSV rows
      const csvRows = [headers.join(",")];

      hits.forEach((hit) => {
        const metadata = hit.metadata || {};

        const title = metadata.title || "";
        const authors =
          (metadata.authors || []).map((a) => a.full_name || "").join("; ") ||
          "";
        const year = metadata.publication_year || "";
        const collectionType = metadata.collection_type || "";
        const department = metadata.academic_info?.department || "";
        const program = metadata.academic_info?.program || "";

        // Escape and quote fields that contain commas or quotes
        const escapeCSV = (field) => {
          const str = String(field || "");
          if (str.includes(",") || str.includes('"') || str.includes("\n")) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        };

        const row = [
          escapeCSV(title),
          escapeCSV(authors),
          escapeCSV(year),
          escapeCSV(collectionType),
          escapeCSV(department),
          escapeCSV(program),
        ].join(",");

        csvRows.push(row);
      });

      const csvContent = csvRows.join("\n");

      // Download CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `thesis_dissertations_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("CSV Export Error:", error);
      alert(`CSV export failed: ${error.message}`);
    }
  };

  customExportPDF = async (currentQueryState, size = 1000) => {
    try {
      const requestSerializer = this.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params["page"] = 1;
      params["size"] = size;
      if (!params["sort"]) {
        params["sort"] = "_created";
      }

      // Fetch JSON data to get all fields including department and program
      const exportUrl = `${specialCollectionsApi.searchBaseURL}?${Qs.stringify(
        params,
        {
          arrayFormat: "repeat",
          encode: false,
        }
      )}`;

      const response = await fetch(exportUrl, { credentials: "include" });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const jsonData = await response.json();
      const hits = jsonData.hits?.hits || [];

      // Build table headers
      const tableHeaders = [
        "Title",
        "Author(s)",
        "Publication Year",
        "Collection Type",
        "Department",
        "Program",
      ]
        .map((h) => `<th>${h}</th>`)
        .join("");

      // Build table rows from JSON data
      const tableRows = hits
        .map((hit) => {
          const metadata = hit.metadata || {};

          const title = metadata.title || "";
          const authors =
            (metadata.authors || []).map((a) => a.full_name || "").join(", ") ||
            "";
          const year = metadata.publication_year || "";
          const collectionType = metadata.collection_type || "";
          const department = metadata.academic_info?.department || "";
          const program = metadata.academic_info?.program || "";

          return `
          <tr>
            <td>${title}</td>
            <td>${authors}</td>
            <td>${year}</td>
            <td>${collectionType}</td>
            <td>${department}</td>
            <td>${program}</td>
          </tr>
        `;
        })
        .join("");

      const htmlContent = generateExportHTML({
        reportTitle: "Thesis and Dissertations Export Report",
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        footerText: "Library Resource Center - Theses and Dissertations",
        reportType: "thesis",
      });

      const blob = new Blob([htmlContent], {
        type: "text/html;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      const printWindow = window.open(url, "_blank");

      setTimeout(() => URL.revokeObjectURL(url), 2000);

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => printWindow.focus(), 500);
        };
      } else {
        alert("Please allow popups for this site to export to PDF");
      }
    } catch (error) {
      alert(`PDF export failed: ${error.message}`);
    }
  };

  render() {
    const helperFields = [
      {
        name: "author",
        field: "authors.full_name",
        defaultValue: '"Smith, Jane"',
      },
      {
        name: "submitted",
        field: "submission_date",
      },
      {
        name: "degree",
        field: "degree_type",
      },
      {
        name: "department",
        field: "department",
      },
    ];

    const initialState = setReactSearchKitInitialQueryState(this.modelName);
    const defaultSortingOnEmptyQueryString =
      setReactSearchKitDefaultSortingOnEmptyQueryString(this.modelName);
    const urlHandler = setReactSearchKitUrlHandler(this.modelName);

    return (
      <>
        <Header as="h2">Theses and Dissertaions</Header>
        <OverridableContext.Provider
          value={{
            ...SearchControlsOverridesMap,
          }}
        >
          <ReactSearchKit
            searchApi={this.searchApi}
            history={history}
            urlHandlerApi={urlHandler}
            initialQueryState={initialState}
            defaultSortingOnEmptyQueryString={defaultSortingOnEmptyQueryString}
          >
            <>
              <SessionFiltersPersistence moduleIdentifier="special-collections" />
              <Container fluid className="spaced">
                <SearchBar
                  placeholder="Search for theses and dissertations..."
                  {...invenioConfig.APP.SEARCH_BAR_PROPS}
                />
                <QueryBuildHelper fields={helperFields} />
              </Container>
              <Container fluid className="bo-search-body">
                <Grid>
                  <Grid.Row columns={2}>
                    <ResultsLoader>
                      <Grid.Column width={3} className="search-aggregations">
                        <div className="search-clear-filters">
                          <Header content="Filter by" />
                          <ClearFiltersButton moduleIdentifier="special-collections" />
                        </div>
                        <SearchAggregationsCards modelName={this.modelName} />
                        <CascadingDropdownFilter />
                      </Grid.Column>
                      <Grid.Column width={13}>
                        <Grid columns={2}>
                          <Grid.Column width={8}>
                            <NewButton
                              text="Add Thesis"
                              to={
                                BackOfficeRoutes.specialColCreate ||
                                "/backoffice/specialCollection/create"
                              }
                            />
                          </Grid.Column>
                          <Grid.Column width={8} textAlign="right">
                            <ExportReactSearchKitResults
                              exportBaseUrl={
                                specialCollectionsApi.searchBaseURL
                              }
                              onExportClick={this.handleExportClick}
                              supportedFormats={["csv", "pdf"]}
                            />
                          </Grid.Column>
                        </Grid>
                        <EmptyResults />
                        <Error />
                        <SearchControls
                          modelName={this.modelName}
                          withLayoutSwitcher={false}
                        />
                        <ResultsList
                          ListEntryElement={SpecialCollectionListEntry}
                        />
                        <SearchFooter />
                      </Grid.Column>
                    </ResultsLoader>
                  </Grid.Row>
                </Grid>
              </Container>
            </>
          </ReactSearchKit>
        </OverridableContext.Provider>
      </>
    );
  }
}

export default SpecialCollectionsSearch;
